import {
  IAppRole,
  ICommonQueryParams,
  IUser,
  IUserListResponse,
  NetworkMethodEnum,
} from "@/types";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { mutator } from "@/utils/api/dataverse/mutator";
import { generateQueryParamsForPagination } from "@/utils/pagination";
import {
  IAppRoleForm,
  IAppRoleListResponse,
  IAssignRole,
  IAssignRoleForm,
  IAssignRoleListResponse,
  IGetUserRolesResponse,
  IUserForm,
} from "../types";

export const URL = {
  appRole: "/AppRoleApi",
  assignRole: "/AssignRole",
  users: "/UsersApi",
};

// ============================================
// APP ROLE APIs
// ============================================

/**
 * Get list of app roles
 */
export const getAppRoleList = async (
  queryParams?: ICommonQueryParams,
): Promise<IAppRoleListResponse> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(`${URL.appRole}${query}`);
};

/**
 * Get single app role by ID
 */
export const getAppRole = async (id: string): Promise<IAppRole> => {
  return await fetcher(`${URL.appRole}?$filter=dogma_approleid eq ${id}`);
};

/**
 * Create a new app role
 */
export const createAppRole = async (data: IAppRoleForm): Promise<IAppRole> => {
  const payload = {
    dogma_name: data.dogma_name,
    dogma_permissionsjson: data.dogma_permissionsjson,
    dogma_defaultsecurityrole: data.dogma_defaultsecurityrole,
    statecode: data.statecode ?? 0,
    statuscode: data.statuscode ?? 1,
  };
  return await mutator(NetworkMethodEnum.POST, URL.appRole, payload);
};

/**
 * Update an existing app role
 */
export const updateAppRole = async (data: {
  id: string;
  payload: Partial<IAppRoleForm>;
}): Promise<IAppRole> => {
  const updatePayload = {
    id: data.id,
    ...data.payload,
  };
  return await mutator(NetworkMethodEnum.PATCH, URL.appRole, updatePayload);
};

/**
 * Delete an app role
 */
export const deleteAppRole = async (id: string): Promise<void> => {
  const payload = { id };
  return await mutator(NetworkMethodEnum.DELETE, URL.appRole, payload);
};

// ============================================
// ASSIGN ROLE APIs
// ============================================

/**
 * Get list of role assignments
 */
export const getAssignRoleList = async (
  queryParams?: ICommonQueryParams,
): Promise<IAssignRoleListResponse> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(`${URL.assignRole}${query}`);
};

/**
 * Get role assignments for a specific user
 * Returns custom API format with success, message, and roles array
 */
export const getUserRoles = async (
  systemuserid: string,
): Promise<IGetUserRolesResponse> => {
  return await fetcher(`${URL.assignRole}?systemuserid=${systemuserid}`);
};

/**
 * Get single role assignment by ID
 */
export const getAssignRole = async (id: string): Promise<IAssignRole> => {
  return await fetcher(`${URL.assignRole}?$filter=systemuserroleid eq ${id}`);
};

/**
 * Assign a role to a user
 */
export const assignRole = async (
  data: IAssignRoleForm,
): Promise<IAssignRole> => {
  const payload = {
    systemUserId: data.systemuserid,
    appRoleId: data.roleid,
  };
  return await mutator(NetworkMethodEnum.POST, URL.assignRole, payload);
};

/**
 * Update a role assignment
 */
export const updateAssignRole = async (data: {
  id: string;
  payload: Partial<IAssignRoleForm>;
}): Promise<IAssignRole> => {
  const updatePayload = {
    id: data.id,
    systemUserId: data.payload.systemuserid,
    appRoleId: data.payload.systemuserid,
  };
  return await mutator(NetworkMethodEnum.PATCH, URL.assignRole, updatePayload);
};

/**
 * Remove a role assignment (unassign role from user)
 */
export const unassignRole = async (body: any): Promise<void> => {
  return await mutator(NetworkMethodEnum.DELETE, URL.assignRole, body);
};

// ============================================
// USERS APIs
// ============================================

/**
 * Get list of users
 */
export const getUserList = async (
  queryParams?: ICommonQueryParams,
): Promise<IUserListResponse> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(
    `${URL.users}${query}&$expand=dogma_AppRole_SystemUser_SystemUser($select=dogma_defaultsecurityrole,dogma_name,dogma_permissionsjson)`,
  );
};

/**
 * Get single user by ID
 */
export const getUser = async (id: string): Promise<IUser> => {
  return await fetcher(`${URL.users}?$filter=systemuserid eq ${id}`);
};

/**
 * Create a new user
 */
export const createUser = async (data: IUserForm): Promise<IUser> => {
  return await mutator(NetworkMethodEnum.POST, URL.users, data);
};

/**
 * Update an existing user
 */
export const updateUser = async (data: {
  id: string;
  payload: Partial<IUserForm>;
}): Promise<IUser> => {
  const updatePayload = {
    id: data.id,
    ...data.payload,
  };
  return await mutator(NetworkMethodEnum.PATCH, URL.users, updatePayload);
};

/**
 * Delete a user
 */
export const deleteUser = async (id: string): Promise<void> => {
  const payload = { id };
  return await mutator(NetworkMethodEnum.DELETE, URL.users, payload);
};

// ==========================================
// User Stats APIs
// ==========================================

const STATS_DEFAULT_FILTER = `contains(domainname,'vhwg.co.uk')`;

/**
 * Get total users count
 */
export const getTotalUsersCount = async (region?: number): Promise<{
  value: Array<{ count: number }>;
}> => {
  const conditions = [STATS_DEFAULT_FILTER];
  if (region !== undefined && region !== null) {
    conditions.push(`dogma_region eq ${region}`);
  }
  const url = `${URL.users}?$apply=filter(${conditions.join(' and ')})/aggregate($count as count)`;
  return await fetcher(url);
};

/**
 * Get active users count (where dogma_userstatus eq 1)
 */
export const getActiveUsersCount = async (region?: number): Promise<{
  value: Array<{ count: number }>;
}> => {
  const conditions = [STATS_DEFAULT_FILTER, `dogma_userstatus eq 1`];
  if (region !== undefined && region !== null) {
    conditions.push(`dogma_region eq ${region}`);
  }
  const url = `${URL.users}?$apply=filter(${conditions.join(' and ')})/aggregate($count as count)`;
  return await fetcher(url);
};

/**
 * Get deactivated users count (where dogma_userstatus eq 2)
 */
export const getDeactivatedUsersCount = async (region?: number): Promise<{
  value: Array<{ count: number }>;
}> => {
  const conditions = [STATS_DEFAULT_FILTER, `dogma_userstatus eq 2`];
  if (region !== undefined && region !== null) {
    conditions.push(`dogma_region eq ${region}`);
  }
  const url = `${URL.users}?$apply=filter(${conditions.join(' and ')})/aggregate($count as count)`;
  return await fetcher(url);
};

/**
 * Get users count by team
 */
export const getUsersCountByTeam = async (region?: number): Promise<{
  value: Array<{ dogma_team: number; count: number }>;
}> => {
  const conditions = [STATS_DEFAULT_FILTER];
  if (region !== undefined && region !== null) {
    conditions.push(`dogma_region eq ${region}`);
  }
  const url = `${URL.users}?$apply=filter(${conditions.join(' and ')})/groupby((dogma_team), aggregate($count as count))`;
  return await fetcher(url);
};

/**
 * Get administrators count (users with Company Admin role)
 * Filters by role name containing "company admin"
 */
export const getAdministratorsCount = async (region?: number): Promise<{
  value: Array<{ count: number }>;
}> => {
  const conditions = [
    STATS_DEFAULT_FILTER,
    `dogma_AppRole_SystemUser_SystemUser/any(r:contains(r/dogma_name,'company admin'))`,
  ];
  if (region !== undefined && region !== null) {
    conditions.push(`dogma_region eq ${region}`);
  }
  const url = `${URL.users}?$apply=filter(${conditions.join(' and ')})/aggregate($count as count)`;
  return await fetcher(url);
};
